import React from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/ReactToastify.css";

// Components
import Navbar from "../components/navbar";
import PersonalDetails from "../components/PersonalDetails";
import EmailAddress from "../components/EmailAddress";
import ProfilePhoneNumber from "../components/ProfilePhoneNumber";
import MailingAddress from "../components/MailingAddress";
import LocationAndRoles from "../components/LocationAndRoles";

// Hooks
import { useUserProfile } from "../hooks/getUserProfile";

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Card horizontal margins for centered layout
 */
const CARD_HORIZONTAL_MARGIN = "122px";

/**
 * Footer right padding to align with card
 * Calculation: container right padding (24px) + card right margin (122px) = 146px
 */
const FOOTER_RIGHT_PADDING = "146px";

/**
 * Redirect delay after successful profile save (in milliseconds)
 */
const REDIRECT_DELAY_MS = 1500;

/**
 * Dashboard route path
 */
const DASHBOARD_ROUTE = "/hcp/dashboard";

/**
 * Title styling configuration
 */
const TITLE_STYLES = {
  fontSize: "18px",
  color: "#353c51",
  fontWeight: 500,
  fontFamily: '"Quicksand", sans-serif',
};

/**
 * Toast notification default configuration
 */
const TOAST_CONFIG = {
  position: "top-right",
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Checks if user has provided biological sex
 * @param {Object} profile - User profile data
 * @returns {boolean}
 */
const hasBiologicalSex = (profile) => Boolean(profile?.sex);

/**
 * Checks if user has provided date of birth
 * @param {Object} profile - User profile data
 * @returns {boolean}
 */
const hasDateOfBirth = (profile) => Boolean(profile?.dateOfBirth);

/**
 * Checks if user has provided language preference
 * @param {Object} profile - User profile data
 * @returns {boolean}
 */
const hasLanguagePreference = (profile) => Boolean(profile?.languagePreference);

/**
 * Checks if user has at least one email address
 * @param {Object} profile - User profile data
 * @returns {boolean}
 */
const hasEmailAddress = (profile) => {
  return Boolean(profile?.workEmail || profile?.homeEmail || profile?.email);
};

/**
 * Checks if user has at least one phone number
 * @param {Object} profile - User profile data
 * @returns {boolean}
 */
const hasPhoneNumber = (profile) => {
  return Boolean(
    profile?.homePhone || profile?.workPhone || profile?.mobilePhone
  );
};

/**
 * Checks if user has at least one mailing address
 * @param {Object} profile - User profile data
 * @returns {boolean}
 */
const hasMailingAddress = (profile) => {
  return Boolean(
    profile?.mailingAddresses && profile?.mailingAddresses.length > 0
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * CompleteProfile Component
 *
 * A page for first-time users to complete their profile information.
 * Validates required fields (marked with red asterisks) before allowing
 * the user to save and proceed to the dashboard.
 *
 * Required fields:
 * - Biological Sex
 * - Date of Birth
 * - Language Preference
 * - At least one Email Address
 * - At least one Phone Number
 * - At least one Mailing Address
 *
 * @component
 */
const CompleteProfile = () => {
  // ============================================================================
  // HOOKS & STATE
  // ============================================================================

  const { data: userProfile, isLoading, refetch } = useUserProfile();
  const history = useHistory();

  // ============================================================================
  // VALIDATION LOGIC
  // ============================================================================

  /**
   * Validates all required profile fields
   * @returns {string[]} Array of missing field names
   */
  const validateProfile = () => {
    const profile = userProfile?.data;
    const missingFields = [];

    // Required individual fields
    if (!hasBiologicalSex(profile)) {
      missingFields.push("Biological Sex");
    }

    if (!hasDateOfBirth(profile)) {
      missingFields.push("Date of Birth");
    }

    if (!hasLanguagePreference(profile)) {
      missingFields.push("Language Preference");
    }

    // Required collection fields (at least one entry required)
    if (!hasEmailAddress(profile)) {
      missingFields.push("Email Address");
    }

    if (!hasPhoneNumber(profile)) {
      missingFields.push("Phone Number");
    }

    if (!hasMailingAddress(profile)) {
      missingFields.push("Mailing Address");
    }

    return missingFields;
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handles the save profile button click
   * Validates required fields and either shows error or redirects to dashboard
   */
  const handleSaveProfile = () => {
    const missingFields = validateProfile();

    // Show error if validation fails
    if (missingFields.length > 0) {
      const fieldsList = missingFields.join(", ");
      toast.error(
        `Please complete the following required fields: ${fieldsList}`,
        {
          ...TOAST_CONFIG,
          autoClose: 5000,
        }
      );
      return;
    }

    // Show success message
    toast.success("Profile completed successfully!", {
      ...TOAST_CONFIG,
      autoClose: 3000,
    });

    // Redirect to dashboard after delay
    setTimeout(() => {
      history.push(DASHBOARD_ROUTE);
    }, REDIRECT_DELAY_MS);
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================

  /**
   * Common props passed to all profile section components
   */
  const commonProfileProps = {
    userProfile: userProfile?.data,
    isLoading,
    refetch,
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="d-flex flex-column h_100">
      {/* Navigation Bar */}
      <Navbar isPageRole={false} />

      {/* Main Content Area */}
      <div className="flex-grow-1 overflow-auto">
        <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
          {/* Centered Card Container */}
          <div
            className="card border-0 h-100"
            style={{
              marginLeft: CARD_HORIZONTAL_MARGIN,
              marginRight: CARD_HORIZONTAL_MARGIN,
            }}
          >
            <div className="card-body mb-3">
              {/* Header Section */}
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={TITLE_STYLES}>
                  User Profile
                </h5>
                <label className="small-text decoration-0">
                  POMMe ID:{" "}
                  <span className="ml-1">
                    {userProfile?.data?.pommeId || "N/A"}
                  </span>
                </label>
              </div>

              <hr />

              {/* Profile Sections */}
              <div className="noScroll h-90 verticalScroll pb-17">
                {/* Location and Roles Section */}
                <LocationAndRoles {...commonProfileProps} />

                {/* Personal Details Section */}
                <PersonalDetails {...commonProfileProps} />

                {/* Email Addresses Section (Required) */}
                <EmailAddress {...commonProfileProps} />

                {/* Phone Numbers Section (Required) */}
                <ProfilePhoneNumber {...commonProfileProps} />

                {/* Mailing Addresses Section (Required) */}
                <MailingAddress {...commonProfileProps} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with Save Button */}
      <footer
        className="d-flex justify-content-end align-items-center bg_primary py-4 h-69"
        style={{ paddingRight: FOOTER_RIGHT_PADDING }}
      >
        <button
          type="button"
          className="btn btn_light px-5"
          onClick={handleSaveProfile}
          disabled={isLoading}
        >
          <small>SAVE PROFILE</small>
        </button>
      </footer>
    </div>
  );
};

export default CompleteProfile;
